import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  useTeamsChatSession,
  useTeamsStatus,
  useTeamsUnreadCount,
} from '../../hooks/useTeamsChatSession';
import { TeamsChatIcon, TeamsCloseIcon, TeamsNewChatIcon, TeamsPopOutIcon } from '@atlas/ui';
import {
  TEAMS_PANEL_DEFAULT_WIDTH,
  TEAMS_PANEL_MIN_WIDTH,
  teamsPanelMaxWidth,
  useTeamsChatStore,
} from '../../stores/teamsChatStore';
import { TeamsChatBody } from './TeamsChatBody';
import { TeamsPanelResizeHandle } from './TeamsPanelResizeHandle';
import { TeamsPersonPicker } from './TeamsPersonPicker';

const APP_BAR_HEIGHT = 56;

/** The standalone page this drawer can hand a conversation off to. */
const TEAMS_CHAT_PATH = '/teams-chat';
/**
 * Named, so a second click focuses the window that is already open instead of
 * scattering copies across the desktop. A different `?chat=` simply navigates
 * the existing one, which is what "pop this conversation out" should do.
 */
const TEAMS_CHAT_WINDOW_NAME = 'atlas-teams-chat';
/** Tall and narrow — the children were laid out against a ~320px column. */
const POPOUT_FEATURES = 'popup=yes,width=420,height=760';

interface TeamsChatToggleProps {
  /** False on the login screen — status is a 401 there, so don't ask. */
  enabled?: boolean;
}

/**
 * AppBar control that opens/closes the Teams sidebar. Renders nothing when the
 * server has no Teams integration configured.
 */
export function TeamsChatToggle({ enabled = true }: TeamsChatToggleProps) {
  const { t } = useTranslation();
  const isOpen = useTeamsChatStore((s) => s.isOpen);
  const toggle = useTeamsChatStore((s) => s.toggle);
  const { configured } = useTeamsStatus(enabled);
  const unreadCount = useTeamsUnreadCount();

  if (!configured) return null;

  const base = isOpen ? t('teams.panel.closeAriaLabel') : t('teams.panel.openAriaLabel');
  // The count goes in the accessible name, not just the badge: a marker that
  // exists only as a coloured circle tells a screen-reader user nothing, and
  // "open Teams chat" while three conversations are waiting is a lie of
  // omission. Counts CONVERSATIONS, not messages — the label says so.
  const label =
    unreadCount > 0 ? `${base} — ${t('teams.panel.unreadCount', { count: unreadCount })}` : base;

  return (
    <Tooltip title={label}>
      <IconButton
        onClick={toggle}
        aria-label={label}
        aria-expanded={isOpen}
        aria-controls="teams-chat-panel"
        size="medium"
        color={isOpen ? 'primary' : 'default'}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <TeamsChatIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

/**
 * The resize ceiling for the current window. Recomputed on resize because the
 * bound is a share of the viewport: dragging a window narrow must tighten the
 * cap, otherwise a panel sized on a big monitor stays oversized on a laptop.
 */
function useMaxPanelWidth(): number {
  const [maxWidth, setMaxWidth] = useState(() => teamsPanelMaxWidth(window.innerWidth));

  useEffect(() => {
    const onResize = () => setMaxWidth(teamsPanelMaxWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return maxWidth;
}

interface TeamsChatPanelProps {
  /** False on the login screen — status is a 401 there, so don't ask. */
  enabled?: boolean;
}

/**
 * Persistent right-hand Teams chat drawer.
 *
 * Mounted in `Layout` OUTSIDE the router outlet, so navigating between pages
 * neither unmounts it nor resets which conversation is open. All server state
 * lives in React Query; only `isOpen` / `activeChatId` live in the store.
 *
 * This component owns only the chrome — the drawer, the header and the resize
 * handle. The data layer is `useTeamsChatSession` and the contents are
 * `TeamsChatBody`, both shared with the standalone `/teams-chat` page.
 */
export function TeamsChatPanel({ enabled = true }: TeamsChatPanelProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  // Below md the drawer would squeeze the page to nothing, so it overlays.
  const isWideEnoughToDock = useMediaQuery(theme.breakpoints.up('md'));

  const isOpen = useTeamsChatStore((s) => s.isOpen);
  const close = useTeamsChatStore((s) => s.close);
  const activeChatId = useTeamsChatStore((s) => s.activeChatId);
  const panelWidth = useTeamsChatStore((s) => s.panelWidth);
  const setPanelWidth = useTeamsChatStore((s) => s.setPanelWidth);

  const maxPanelWidth = useMaxPanelWidth();
  // Both nodes have to agree: the root reserves the layout space the page
  // reflows around, the paper is what you actually see. Drift between them is a
  // gap or an overlap, so they are always written together.
  const rootRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  /**
   * Apply a width without going through React. A drag emits a move event per
   * frame, and routing that through state re-renders the whole panel subtree —
   * chat list, thread, composer — on every one of them. Two style writes cost
   * nothing by comparison; the store only hears about it on release.
   */
  const handlePreviewWidth = useCallback((next: number) => {
    const px = `${next}px`;
    if (rootRef.current) rootRef.current.style.width = px;
    if (paperRef.current) paperRef.current.style.width = px;
  }, []);

  // The committed width is applied the same imperative way, so a keyboard
  // resize and the end of a drag land in exactly one place and cannot disagree
  // with whatever the last preview wrote.
  useEffect(() => {
    const root = rootRef.current;
    const paper = paperRef.current;
    if (!root || !paper) return;
    if (!isWideEnoughToDock) {
      // Overlay mode is full-width by design — hand the sizing back to `sx`.
      root.style.width = '';
      paper.style.width = '';
      return;
    }
    root.style.width = isOpen ? `${panelWidth}px` : '0px';
    paper.style.width = `${panelWidth}px`;
  }, [panelWidth, isWideEnoughToDock, isOpen]);

  // A window that shrank below twice the panel width leaves a stored value over
  // the new ceiling. Push it back through the store so the persisted copy is
  // corrected too, not just the pixels.
  useEffect(() => {
    if (isWideEnoughToDock && panelWidth > maxPanelWidth) setPanelWidth(maxPanelWidth);
  }, [isWideEnoughToDock, panelWidth, maxPanelWidth, setPanelWidth]);

  // The drawer is the only "is anyone looking at this" signal here; the
  // standalone page passes a constant true.
  const session = useTeamsChatSession({ enabled, isVisible: isOpen });

  const popOutHref = activeChatId
    ? `${TEAMS_CHAT_PATH}?chat=${encodeURIComponent(activeChatId)}`
    : TEAMS_CHAT_PATH;
  const [isPopOutBlocked, setIsPopOutBlocked] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handlePopOut = useCallback(() => {
    // `window.open` returns null when a popup blocker refuses it. Nothing was
    // opened in that case, so the drawer must stay exactly where it is —
    // closing it would take the chat away and give nothing back.
    const opened = window.open(popOutHref, TEAMS_CHAT_WINDOW_NAME, POPOUT_FEATURES);
    if (!opened) {
      setIsPopOutBlocked(true);
      return;
    }
    opened.focus();
    setIsPopOutBlocked(false);
    // Two live copies of the same conversation on one screen is confusing and
    // doubles the Graph traffic for no benefit.
    close();
  }, [popOutHref, close]);

  if (!session.configured) return null;

  return (
    <Drawer
      id="teams-chat-panel"
      anchor="right"
      open={isOpen}
      onClose={close}
      // Docked on md+ so the page reflows beside it; overlaid below md, where
      // 320px of a phone viewport would leave the content unusable.
      variant={isWideEnoughToDock ? 'persistent' : 'temporary'}
      ModalProps={{ keepMounted: true }}
      aria-label={t('teams.panel.title')}
      ref={rootRef}
      sx={{
        width: isOpen ? { xs: 0, md: panelWidth } : 0,
        flexShrink: 0,
      }}
      slotProps={{
        paper: {
          ref: paperRef,
          sx: {
            width: { xs: '100%', sm: panelWidth },
            maxWidth: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: 1,
            borderColor: 'divider',
            mt: { xs: 0, md: `${APP_BAR_HEIGHT}px` },
            height: { xs: '100%', md: `calc(100% - ${APP_BAR_HEIGHT}px)` },
          },
        },
      }}
    >
      {/* Docked only. Below md the drawer already fills the viewport, so there
          is no width left to negotiate. */}
      {isWideEnoughToDock && (
        <TeamsPanelResizeHandle
          width={panelWidth}
          minWidth={TEAMS_PANEL_MIN_WIDTH}
          maxWidth={maxPanelWidth}
          defaultWidth={TEAMS_PANEL_DEFAULT_WIDTH}
          onPreviewWidth={handlePreviewWidth}
          onCommitWidth={setPanelWidth}
        />
      )}

      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <TeamsChatIcon fontSize="small" />
        <Typography variant="subtitle2" component="h2" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {t('teams.panel.title')}
        </Typography>
        {/* Hidden until the connection is live: a picker that can only fail is
            worse than no button, and the not-connected panel already offers the
            one action that helps. */}
        {session.connected && !session.needsReconnect && (
          <Tooltip title={t('teams.panel.newChatAriaLabel')}>
            <IconButton
              onClick={() => setIsPickerOpen(true)}
              aria-label={t('teams.panel.newChatAriaLabel')}
              size="small"
            >
              <TeamsNewChatIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('teams.panel.popOutAriaLabel')}>
          <IconButton
            onClick={handlePopOut}
            aria-label={t('teams.panel.popOutAriaLabel')}
            size="small"
          >
            <TeamsPopOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          onClick={close}
          aria-label={t('teams.panel.closeAriaLabel')}
          size="small"
          edge="end"
        >
          <TeamsCloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {isPopOutBlocked && (
        // The blocker is a browser setting we cannot talk our way past, so say
        // what happened and offer the same destination as an ordinary link —
        // a navigation the blocker has no say over.
        <Alert
          severity="warning"
          onClose={() => setIsPopOutBlocked(false)}
          sx={{ borderRadius: 0, '& .MuiAlert-message': { minWidth: 0 } }}
        >
          <Typography variant="body2">{t('teams.panel.popOutBlocked')}</Typography>
          <RouterLink to={popOutHref} onClick={close}>
            {t('teams.panel.popOutBlockedCta')}
          </RouterLink>
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TeamsChatBody session={session} />
      </Box>

      <TeamsPersonPicker
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        // Straight into the conversation — the point of "new chat" is to be
        // talking to somebody, not to be looking at a list with them in it.
        onChatCreated={(chat) => session.selectChat(chat.id)}
      />
    </Drawer>
  );
}
